/**
 * The farm's parameter bands (#347): one table behind entry colours, the fill-map and the
 * readings warnings. The numbers come from GET /farms/{id}/thresholds (the server owns the
 * defaults and the farm's overrides); nothing here knows a default. Keys are reading columns
 * (`ph`, `dissolved_oxygen`, ...), the same keys the API validates with.
 */

export interface Band {
  rec_min: number | null;
  rec_max: number | null;
  crit_min: number | null;
  crit_max: number | null;
  conditions?: BandCondition[];
}

export interface BandCondition {
  when: { param: string; gte?: number | null; lt?: number | null };
  rec_min?: number | null;
  rec_max?: number | null;
  crit_min?: number | null;
  crit_max?: number | null;
}

export type ThresholdTable = Record<string, Band>;
export type Tier = 'ok' | 'warning' | 'critical';
export type BandField = 'rec_min' | 'rec_max' | 'crit_min' | 'crit_max';
export const BAND_FIELDS: BandField[] = ['crit_min', 'rec_min', 'rec_max', 'crit_max'];

/** The reading's own values (db column -> value), for conditional bands. */
export type Context = Record<string, number | string | null | undefined>;

function activeBand(band: Band, ctx: Context | undefined): Band {
  for (const cond of band.conditions ?? []) {
    const raw = ctx?.[cond.when.param];
    const x = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
    if (Number.isNaN(x)) continue;
    // The API accepts a bound left out of `when` as open, same as null.
    const { gte, lt } = cond.when;
    if ((gte === null || gte === undefined || x >= gte) && (lt === null || lt === undefined || x < lt)) {
      return { ...band, ...pickBandFields(cond) };
    }
  }
  return band;
}

function pickBandFields(cond: BandCondition): Partial<Band> {
  const out: Partial<Band> = {};
  for (const f of BAND_FIELDS) if (f in cond) out[f] = cond[f] ?? null;
  return out;
}

const below = (v: number, lo: number | null) => lo !== null && v < lo;
const above = (v: number, hi: number | null) => hi !== null && v > hi;

const warnedMissing = new Set<string>();

/** Tier for a value. A null bound is open-ended, never "unknown"; no table (not loaded) or no
 *  band for the column is 'ok', so nothing is coloured on a guess. A loaded table with no band for a
 *  judged column means the server and this client disagree on the parameter list: it is logged once
 *  per column instead of passing silently. `reject` is server-only. */
export function evaluateTier(
  table: ThresholdTable | undefined,
  column: string,
  value: number | null | undefined,
  ctx?: Context
): Tier {
  if (!table || value === null || value === undefined || Number.isNaN(value)) return 'ok';
  const base = table[column];
  if (!base) {
    if (!warnedMissing.has(column)) {
      warnedMissing.add(column);
      console.warn(`thresholds: the farm's table has no band for "${column}"; it is not judged`);
    }
    return 'ok';
  }
  const band = activeBand(base, ctx);
  if (below(value, band.crit_min) || above(value, band.crit_max)) return 'critical';
  if (below(value, band.rec_min) || above(value, band.rec_max)) return 'warning';
  return 'ok';
}

export function recommendedBand(table: ThresholdTable | undefined, column: string, ctx?: Context): { min: number | null; max: number | null } | null {
  const base = table?.[column];
  if (!base) return null;
  const band = activeBand(base, ctx);
  if (band.rec_min === null && band.rec_max === null) return null;
  return { min: band.rec_min, max: band.rec_max };
}

/** Inline helper under a cell, e.g. "rec 7.8–8.2", "rec ≥5", "rec <1". */
export function recommendedLabel(table: ThresholdTable | undefined, column: string, ctx?: Context): string | null {
  const r = recommendedBand(table, column, ctx);
  if (!r) return null;
  if (r.min !== null && r.max !== null) return `rec ${r.min}–${r.max}`;
  if (r.min !== null) return `rec ≥${r.min}`;
  return `rec <${r.max}`;
}

/** "Usual range: 7.8 to 8.2.", "Usual: at least 5.", "Usual: up to 1.0." from the yellow band only. */
export function usualRangeSentence(
  table: ThresholdTable | undefined,
  column: string,
  decimalStep: boolean,
  ctx?: Context
): string | null {
  const r = recommendedBand(table, column, ctx);
  if (!r) return null;
  if (r.min !== null && r.max !== null) return `Usual range: ${r.min} to ${r.max}.`;
  if (r.min !== null) return `Usual: at least ${r.min}.`;
  return `Usual: up to ${decimalStep && Number.isInteger(r.max) ? r.max!.toFixed(1) : r.max}.`;
}
